import React, { useMemo, useState } from 'react';
import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { tokens } from '../../theme/tokens';

/** Bounds the API enforces (src/validators/storeOverrideSpecs.ts). */
export const STORE_SPECS_MAX_COUNT = 50;
export const STORE_SPEC_LABEL_MAX = 80;
export const STORE_SPEC_VALUE_MAX = 500;

export interface StoreSpecsEditorProps {
  /** The stored `overrideSpecs` (label -> value). */
  specs: Record<string, string>;
  /** Labels to offer in the picker: the normalized catalog labels the push matches (`GET .../spec-labels`). */
  labelOptions: string[];
  /** Writes the whole map; the parent saves it and refreshes `specs`. */
  onSave: (next: Record<string, string>) => void;
  pending: boolean;
  disabled?: boolean;
}

/**
 * The store's own spec values for this product (flag `store_override_specs_push`, MOT-413): add,
 * edit and remove a label -> value. Each change saves the whole map at once. They never change the
 * catalog; an integration with "Push my own spec values" on writes them to the spec fields the
 * store already mapped on the next sync.
 */
export function StoreSpecsEditor({ specs, labelOptions, onSave, pending, disabled }: StoreSpecsEditorProps) {
  const { t } = useAppTranslation();
  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newValue, setNewValue] = useState('');

  const entries = Object.entries(specs);
  const takenLabels = useMemo(() => new Set(entries.map(([label]) => label.toLowerCase())), [entries]);
  const options = useMemo(() => labelOptions.filter((label) => !takenLabels.has(label.toLowerCase())), [labelOptions, takenLabels]);

  const trimmedLabel = newLabel.trim();
  const trimmedValue = newValue.trim();
  const duplicate = trimmedLabel !== '' && takenLabels.has(trimmedLabel.toLowerCase());
  const full = entries.length >= STORE_SPECS_MAX_COUNT;
  const canAdd = !disabled && !pending && !full && !duplicate && trimmedLabel !== '' && trimmedValue !== '';
  const addHelper = duplicate
    ? t('store.ownSpecs.duplicate')
    : full
      ? t('store.ownSpecs.full', { max: STORE_SPECS_MAX_COUNT })
      : undefined;

  const add = () => {
    if (!canAdd) return;
    onSave({ ...specs, [trimmedLabel]: trimmedValue });
    setNewLabel('');
    setNewValue('');
  };
  const saveEdit = (label: string) => {
    const value = editValue.trim();
    if (!value) return;
    onSave({ ...specs, [label]: value });
    setEditing(null);
  };
  const remove = (label: string) => {
    const { [label]: _removed, ...rest } = specs;
    onSave(rest);
  };

  return (
    <Box
      data-testid="store-own-specs"
      sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '12px', borderRadius: '10px', border: `1px solid ${tokens.line.hairline}`, bgcolor: tokens.surface.subtle }}
    >
      <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: 'text.primary' }}>{t('store.ownSpecs.title')}</Typography>
      <Typography sx={{ fontSize: '12px', lineHeight: 1.5, color: tokens.ink.muted }}>{t('store.ownSpecs.intro')}</Typography>

      {entries.length === 0 && (
        <Typography sx={{ fontSize: '12.5px', color: tokens.ink.muted }}>{t('store.ownSpecs.empty')}</Typography>
      )}
      {entries.map(([label, value]) => (
        <Box key={label} data-testid="store-own-spec-row" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.faint }}>{label}</Typography>
            {editing === label ? (
              <TextField
                size="small"
                fullWidth
                autoFocus
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveEdit(label);
                  if (e.key === 'Escape') setEditing(null);
                }}
                inputProps={{ maxLength: STORE_SPEC_VALUE_MAX, 'aria-label': `${t('store.ownSpecs.value')} ${label}` }}
              />
            ) : (
              <Typography sx={{ fontSize: '13px', color: 'text.primary', wordBreak: 'break-word' }}>{value}</Typography>
            )}
          </Box>
          {editing === label ? (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={() => saveEdit(label)}
                disabled={disabled || pending || !editValue.trim()}
                sx={{ textTransform: 'none', fontSize: '12px' }}
              >
                {t('store.ownSpecs.save')}
              </Button>
              <Button size="small" onClick={() => setEditing(null)} sx={{ textTransform: 'none', fontSize: '12px', color: 'text.secondary' }}>
                {t('store.ownSpecs.cancel')}
              </Button>
            </>
          ) : (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setEditing(label);
                  setEditValue(value);
                }}
                disabled={disabled || pending}
                aria-label={`${t('store.ownSpecs.edit')} ${label}`}
                sx={{ textTransform: 'none', fontSize: '12px' }}
              >
                {t('store.ownSpecs.edit')}
              </Button>
              <Button
                size="small"
                onClick={() => remove(label)}
                disabled={disabled || pending}
                aria-label={`${t('store.ownSpecs.remove')} ${label}`}
                sx={{ textTransform: 'none', fontSize: '12px', color: 'text.secondary' }}
              >
                {t('store.ownSpecs.remove')}
              </Button>
            </>
          )}
        </Box>
      ))}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
        <Autocomplete
          freeSolo
          size="small"
          options={options}
          inputValue={newLabel}
          onInputChange={(_e, value) => setNewLabel(value.slice(0, STORE_SPEC_LABEL_MAX))}
          disabled={disabled || pending || full}
          sx={{ flex: '1 1 180px', minWidth: 0 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('store.ownSpecs.label')}
              placeholder={t('store.ownSpecs.labelPlaceholder')}
              error={duplicate}
              helperText={addHelper}
            />
          )}
        />
        <TextField
          size="small"
          label={t('store.ownSpecs.value')}
          placeholder={t('store.ownSpecs.valuePlaceholder')}
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') add();
          }}
          disabled={disabled || pending || full}
          inputProps={{ maxLength: STORE_SPEC_VALUE_MAX }}
          sx={{ flex: '1 1 180px', minWidth: 0 }}
        />
        <Button size="small" variant="outlined" onClick={add} disabled={!canAdd} sx={{ textTransform: 'none', fontSize: '12px', mt: 0.5 }}>
          {t('store.ownSpecs.add')}
        </Button>
      </Box>
      <Typography sx={{ fontSize: '11.5px', color: tokens.ink.muted }}>{t('store.ownSpecs.pushHint')}</Typography>
    </Box>
  );
}
